import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { Alert, ICONS, Icon, Modal, useStyleSheet, KIT_CSS } from '../invoices/kit';
import { FEEDBACK_CSS } from './feedbackCss';
import Field from './Field';
import ShotPicker from './ShotPicker';
import Choices from './Choices';
import { FEEDBACK_ICONS } from './icons';
import {
  IMPACT_CHOICES,
  MAX_DETAIL,
  MAX_TITLE,
  TYPE_CHOICES,
  clientContext,
  draftPayload,
  emptyDraft,
  remaining,
  updateDraft,
  validateDraft,
} from './lib/feedbackDraft';
import { createFeedback } from '../../api_handlers/feedback';
import { markOneSeen } from './lib/unread';
import mixpanel from '../../utils/mixpanel';

/**
 * The box people actually type into.
 *
 * Four fields and nothing else. Every extra field is a reason not to
 * bother, and the two that would be most tempting to add -- which screen,
 * and what browser -- are captured without asking.
 *
 * Errors appear per field and only after a submit attempt, so the form is
 * not scolding someone who is still halfway through filling it in.
 */

function Counter({ value, max }) {
  const left = remaining(value, max);
  return (
    <span className={`fb-count${left < max * 0.1 ? ' fb-count--low' : ''}`}>{left} left</span>
  );
}

export default function FeedbackModal({ open, onClose, onSubmitted }) {
  useStyleSheet('iv-kit', KIT_CSS);
  useStyleSheet('fb-kit', FEEDBACK_CSS);

  const [draft, setDraft] = useState(emptyDraft);
  const [shots, setShots] = useState([]);
  const [errors, setErrors] = useState({});
  const [tried, setTried] = useState(false);
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState('');
  const [done, setDone] = useState(null);

  // Captured when the modal opens, not when it is submitted: by the time
  // someone finishes typing they may have navigated behind the scrim.
  const context = useMemo(() => (open ? clientContext() : null), [open]);

  useEffect(() => {
    if (open) return;
    // Reset on close so the next report starts blank rather than carrying
    // the last one's answers.
    setDraft(emptyDraft());
    setShots([]);
    setErrors({});
    setTried(false);
    setSaving(false);
    setFailure('');
    setDone(null);
  }, [open]);

  const set = (field, value) => {
    const next = updateDraft(draft, field, value);
    setDraft(next);
    if (tried) setErrors(validateDraft(next).errors);
  };

  const submit = async () => {
    const check = validateDraft(draft);
    setTried(true);
    setErrors(check.errors);
    if (!check.ok || saving) return;

    setSaving(true);
    setFailure('');
    try {
      const saved = await createFeedback(draftPayload(draft, context), shots);
      // They wrote it, so it is read. Without this their own item shows an
      // "updated" dot the first time they open the list.
      markOneSeen(null, saved);
      mixpanel.track('Feedback-Submitted', {
        type: draft.type,
        impact: draft.impact,
        screen: (context && context.screen) || '',
        screenshots: shots.length,
      });
      setDone(saved || {});
      if (onSubmitted) onSubmitted(saved);
    } catch (error) {
      setFailure(error.message || 'We could not save that. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (done) {
    return (
      <Modal
        open={open}
        onClose={onClose}
        title="Thanks — that's logged"
        size="sm"
        tone="feedback"
        footer={
          <>
            <Link className="iv-btn iv-btn--ghost" to="/planner/feedback" onClick={onClose}>
              See my requests
            </Link>
            <button type="button" className="iv-btn iv-btn--primary" onClick={onClose}>
              Done
            </button>
          </>
        }
      >
        <div className="fb-ok">
          <span className="fb-ok__mark">
            <Icon path={ICONS.check} size={22} />
          </span>
          <p className="fb-note">
            It is on the team&apos;s board now. You can follow what happens to it — the status and
            any reply show up on your requests page.
          </p>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Tell us what would make this better"
      subtitle="It goes straight to the product team, with the screen you are on attached."
      size="md"
      tone="feedback"
      footer={
        <>
          <button type="button" className="iv-btn iv-btn--ghost" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="button" className="iv-btn iv-btn--primary" onClick={submit} disabled={saving}>
            {saving ? <span className="iv-spin" /> : null}
            {saving ? 'Sending' : 'Send it'}
          </button>
        </>
      }
    >
      <div className="fb-form">
        <p className="fb-hero">
          <span className="fb-hero__mark">
            <Icon path={FEEDBACK_ICONS.idea} size={16} />
          </span>
          Rough edges are worth reporting even when they feel small. The small ones are the ones
          nobody has told us about.
        </p>

        {failure ? <Alert>{failure}</Alert> : null}

        <Choices
          legend="What is this?"
          name="type"
          choices={TYPE_CHOICES}
          value={draft.type}
          onChange={set}
          error={errors.type}
        />

        <Field
          id="fb-title"
          label="In one line"
          counter={<Counter value={draft.title} max={MAX_TITLE} />}
          error={errors.title}
        >
          <input
            className={`iv-input${errors.title ? ' iv-input--invalid' : ''}`}
            value={draft.title}
            maxLength={MAX_TITLE}
            placeholder="Shortlist export drops the agreed price column"
            onChange={(e) => set('title', e.target.value)}
          />
        </Field>

        <Field
          id="fb-detail"
          label="What happened, and what you expected"
          counter={<Counter value={draft.detail} max={MAX_DETAIL} />}
          error={errors.detail}
        >
          <textarea
            className={`iv-textarea${errors.detail ? ' iv-textarea--invalid' : ''}`}
            value={draft.detail}
            rows={5}
            maxLength={MAX_DETAIL}
            placeholder="I exported the Diwali shortlist to CSV and the agreed price column was empty, though every row shows a price on screen. I expected the same numbers in the file."
            onChange={(e) => set('detail', e.target.value)}
          />
        </Field>

        <Choices
          legend="How much is this costing you?"
          name="impact"
          choices={IMPACT_CHOICES}
          value={draft.impact}
          onChange={set}
          error={errors.impact}
          meter
        />

        <ShotPicker shots={shots} onChange={setShots} />

        <div className="fb-context">
          <Icon path={ICONS.eye} size={15} />
          <p className="fb-context__text">
            We will attach the screen you came from —{' '}
            <span className="fb-context__screen">
              {(context && context.screen_label) || 'this page'}
            </span>
            {' '}— and your browser version. No filters, search terms or names from the page are
            sent.
          </p>
        </div>
      </div>
    </Modal>
  );
}
